import type { JsonObject } from "@nexra/contracts";
import type { LanguageSandboxMode } from "./api";

export const STUDIO_TOOLS = [{ id: "ping", label: "ping" }] as const;

const object = (value: unknown): JsonObject => value && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : {};

/** Controls follow the selected model's endpoint schema, not all models sharing a protocol. */
export function languageStudioControls(schema?: JsonObject, input?: JsonObject) {
  const fields = object(schema?.properties);
  const thinking = object(object(fields.thinking).properties);
  const responsesEffort = object(object(fields.reasoning).properties).effort;
  const generation = object(object(fields.generationConfig).properties);
  const nativeThinking = object(object(generation.thinkingConfig).properties);
  const nativeEffort = nativeThinking.thinkingLevel;
  const effort = object(fields.reasoning_effort ?? responsesEffort ?? object(object(fields.output_config).properties).effort ?? nativeEffort);
  const tokens = object(generation.maxOutputTokens ?? (input && "max_completion_tokens" in input ? fields.max_completion_tokens : fields.max_tokens ?? fields.max_output_tokens ?? fields.max_completion_tokens));
  const temperature = fields.generationConfig ? generation.temperature : fields.temperature;
  const adaptiveThinking = object(thinking.type).const === "adaptive"
    || (Array.isArray(object(thinking.type).enum) && (object(thinking.type).enum as unknown[]).includes("adaptive"));
  return {
    adaptiveThinking,
    reasoningSummary: adaptiveThinking || Boolean(nativeEffort),
    reasoningBudget: !nativeEffort && nativeThinking.thinkingBudget !== false,
    nativeThinkingSwitch: Array.isArray(object(thinking.type).enum) && (object(thinking.type).enum as unknown[]).includes("disabled"),
    effortField: "reasoning_effort" in fields ? "reasoning_effort" : responsesEffort ? "reasoning.effort" : nativeEffort ? "generationConfig.thinkingConfig.thinkingLevel" : "output_config.effort",
    effortDefault: effort.default,
    temperature: schema === undefined || (Boolean(temperature) && typeof temperature === "object" && object(temperature).const === undefined && object(temperature).readOnly !== true),
    temperatureMax: Number(object(temperature).maximum ?? 2),
    maxTokens: Number(tokens.maximum ?? 65536),
    minTokens: Number(tokens.minimum ?? 1),
    efforts: Array.isArray(effort.enum) ? effort.enum.filter((value): value is string => typeof value === "string") : [],
  };
}

/** Built-in tool definitions adapted to each wire protocol. */
export function studioToolDefinitions(protocol: string, selectedTools: string[]): JsonObject[] {
  const builtins = STUDIO_TOOLS.map((tool): JsonObject => {
    const definition = { name: tool.id, description: "Connectivity test tool. Echoes the supplied value.", parameters: { type: "object", properties: { value: { type: "string" } }, required: ["value"], additionalProperties: false } };
    if (protocol === "anthropic-messages") return { name: definition.name, description: definition.description, input_schema: definition.parameters };
    if (protocol === "gemini-generate-content") return { functionDeclarations: [definition] };
    if (protocol === "openai-chat-completions") return { type: "function", function: definition };
    return { type: "function", ...definition, strict: true };
  });
  return builtins.filter((_, index) => selectedTools.includes(STUDIO_TOOLS[index]!.id));
}

/** Tools, reasoning and streaming are independent options on one native request. */
export function studioRunOptions(input: JsonObject, protocol: string, reasoning: boolean, selectedTools: string[], reasoningTokens?: number, schema?: JsonObject, stream = true): { input: JsonObject; checks: LanguageSandboxMode[] } {
  const body = structuredClone(input);
  if (body.model === "") delete body.model;
  const controls = languageStudioControls(schema, body);
  const adaptive = protocol === "anthropic-messages" && controls.adaptiveThinking;
  if (protocol === "gemini-generate-content" && controls.effortField === "generationConfig.thinkingConfig.thinkingLevel") {
    const config = object(body.generationConfig);
    const thinking = object(config.thinkingConfig);
    body.generationConfig = { ...config, thinkingConfig: {
      ...(controls.effortDefault !== undefined ? { thinkingLevel: controls.effortDefault } : {}),
      ...thinking,
      includeThoughts: reasoning,
    } };
  } else if (protocol === "openai-chat-completions" && controls.nativeThinkingSwitch) {
    body.thinking = { ...object(body.thinking), type: reasoning ? "enabled" : "disabled" };
    // A disabled effort takes precedence upstream; re-enabling the checkbox must re-enable thinking.
    if (reasoning && (body.reasoning_effort === undefined || ["none", "minimal"].includes(String(body.reasoning_effort)))) body.reasoning_effort = object(object(schema?.properties).reasoning_effort).default ?? "medium";
  } else if (adaptive && (reasoning || body.thinking !== undefined)) {
    // A native between_tools request has no display/budget fields. Preserve it
    // unless the user explicitly asks for visible adaptive thinking.
    if (reasoning || object(body.thinking).type !== "between_tools") {
      const { budget_tokens: _budget, ...thinking } = object(body.thinking);
      body.thinking = { ...thinking, type: "adaptive", display: reasoning ? "summarized" : "omitted" };
    }
  } else if (reasoning && reasoningTokens !== undefined) {
    const budget = Math.max(1024, Math.floor(reasoningTokens));
    if (protocol === "anthropic-messages") {
      body.thinking = { type: "enabled", budget_tokens: budget };
      body.max_tokens = Math.max(Number(body.max_tokens) || 8192, budget + 512);
    } else if (protocol === "gemini-generate-content") {
      const config = (body.generationConfig ?? {}) as JsonObject;
      body.generationConfig = { ...config, thinkingConfig: { thinkingBudget: budget, includeThoughts: true } };
    }
  }
  const builtins = studioToolDefinitions(protocol, STUDIO_TOOLS.map(tool => tool.id));
  const tools = studioToolDefinitions(protocol, selectedTools);
  const existing = Array.isArray(body.tools) ? body.tools : [];
  const retained = existing.filter(tool => !builtins.some(builtin => JSON.stringify(builtin) === JSON.stringify(tool)));
  if (tools.length || retained.length) body.tools = [...retained, ...tools];
  else if (existing.length) delete body.tools;
  if (protocol !== "gemini-generate-content") body.stream = stream;
  return { input: body, checks: [stream ? "sse" : "text", ...(reasoning ? ["reasoning" as const] : []), ...(tools.length ? ["tool_call" as const] : [])] };
}

/** JSON mode submits the edited native body verbatim; only the transport mode is inferred. */
export function studioJsonRunOptions(input: JsonObject, streamSupported: boolean): { input: JsonObject; checks: LanguageSandboxMode[] } {
  return { input, checks: [streamSupported && input.stream !== false ? "sse" : "text"] };
}
